// Pagina "Dobra de Nicho" — o grafo interativo de oportunidades de um canal.
// Gate premium+flag, input de canal/link, loading por etapas (polling), grafo
// (@xyflow/react) e painel lateral com os detalhes/titulos de cada oportunidade.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Network, Search, RefreshCw, Loader2, X, ArrowRight, ArrowLeft, AlertTriangle, AlertCircle, Copy, Check, ArrowDown, History, Sparkles, Globe, Plus, Rocket, Trash2, Users, User, Lightbulb, Download, FileText, Info, CalendarClock, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Sidebar from '../Sidebar';
import Header from '../Header';
import PageHeader from '../ui/PageHeader';
import FeatureUpsellPage from '../ui/FeatureUpsellPage';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import NichoFoldGraph from './NichoFoldGraph';
import { startAnalysis, getStatus, expandOpportunity, expandNiche, listAnalyses, hideAnalysis } from '../../lib/dobraNicho';
import { downloadTxt, downloadPdf } from '../../lib/dobraExport';
import { formatRelativeTime } from '../niche-finder/utils';

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;

export default function DobraNichoPage({ onLogout, isSuperAdmin }) {
  const { t, i18n } = useTranslation();
  usePageTitle(t('dobraNicho.title'));
  const { sidebarWidth, isMobile } = useSidebar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userLang = (i18n.language || 'pt').slice(0, 2);

  const [accessCheck, setAccessCheck] = useState({ loading: true, hasAccess: false, canGenerate: false });
  const [proModal, setProModal] = useState(false);    // modal "gerar é exclusivo do Pro"
  const [howtoOpen, setHowtoOpen] = useState(false);  // "Como aplicar" recolhido por padrão (não tampa o grafo)
  const [channelInput, setChannelInput] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [analysis, setAnalysis] = useState(null);      // resposta /status completa
  const [reports, setReports] = useState({});          // index -> report (Prompt 2)
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [nicheLoading, setNicheLoading] = useState({}); // "idx.j" -> true (gerando títulos)
  const [panel, setPanel] = useState(null);            // {kind, oppIndex, nicheIndex}
  const [history, setHistory] = useState([]);          // minhas análises (do usuário)
  const [searchQuery, setSearchQuery] = useState('');  // busca client-side em "Minhas análises"
  const [jobError, setJobError] = useState(null);      // falha persistente
  const [jobErrorRetryable, setJobErrorRetryable] = useState(true); // mostra "tentar de novo"?
  const expandingRef = useRef(new Set());              // guarda contra clique duplo
  const currentJobIdRef = useRef(null);                // análise atual (guard p/ callbacks obsoletos)
  const grafoRevealedRef = useRef(false);              // grafo já revelado (modal híbrido)
  const graphWrapRef = useRef(null);                   // wrapper do grafo (rolar até ele ao clicar numa bola)
  // [Comunidade descontinuada] galeria/paginação da comunidade removidas — cada
  // análise é individual e fica só em "Minhas análises".

  // Ao clicar numa bola, rola a página até o grafo ficar todo visível (as bolas
  // ficavam abaixo da dobra do DNA + "como aplicar"). block:'start' + scroll-margin
  // (no wrapper) coloca o topo do grafo logo abaixo do header fixo.
  const scrollGraphIntoView = () => {
    requestAnimationFrame(() => {
      graphWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Mantém o ref do job atual em sincronia com o state — callbacks assíncronos
  // em voo (expandNiche etc.) comparam contra ele e ignoram se a análise mudou.
  useEffect(() => { currentJobIdRef.current = jobId; }, [jobId]);

  const refreshHistory = () => {
    // /analyses devolve job_id; normaliza pra `id` pra reabrir/apagar funcionar.
    listAnalyses().then((rows) => setHistory(rows.map((r) => ({ ...r, id: r.job_id })))).catch(() => {});
  };

  // [Comunidade descontinuada] busca da comunidade removida. A busca em "Minhas
  // análises" é client-side, feita inline no render sobre `history` (já carregado).

  // ── Gate de acesso (premium E flag dobra_nicho_liberado) ──────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/auth/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setAccessCheck({
          loading: false,
          hasAccess: !!data.is_super_admin || !!data.dobra_nicho_acesso,
          // GERAR análise nova = só pro/plus (+ super admin). basic/lite veem mas
          // não geram -> modal "exclusivo do Pro".
          canGenerate: !!data.is_super_admin || !!data.dobra_nicho_can_generate,
        });
      } catch {
        if (!cancelled) setAccessCheck({ loading: false, hasAccess: false, canGenerate: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Carrega o histórico + RETOMA uma análise em andamento (roda em 2º plano no
  // backend). Se o usuário saiu e voltou, recupera o job salvo: se ainda está
  // rodando, volta a fazer polling; se concluiu, abre o resultado.
  useEffect(() => {
    if (!accessCheck.hasAccess) return;
    refreshHistory();
    // Se veio um deep-link ?a=<id> (da página de análises), o efeito dedicado
    // abre essa análise — não restaura a última automaticamente aqui.
    if (searchParams.get('a')) return;
    (async () => {
      // 1) Job EM ANDAMENTO (rodando em 2º plano) — retoma o polling.
      const active = localStorage.getItem('dobra_active_job');
      if (active) {
        try {
          const data = await getStatus(active);
          if (data.status === 'running' || data.status === 'pending') {
            setJobId(active);
            setProgress(data.progress || 0);
            setProgressMessage(data.message || '');
            setAnalyzing(true); // retoma o polling
            return;
          }
          localStorage.removeItem('dobra_active_job');
          if (data.status === 'completed') {
            setJobId(active);
            grafoRevealedRef.current = true;
            setAnalysis(data);
            setReports(data.result?.reports || {});
            localStorage.setItem('dobra_last_job', active);
            return;
          }
        } catch {
          localStorage.removeItem('dobra_active_job');
        }
      }
      // 2) Sem job ativo — RESTAURA a última análise concluída (sobrevive ao
      // refresh: o usuário volta e o grafo + card de DNA continuam na tela).
      const last = localStorage.getItem('dobra_last_job');
      if (!last) return;
      try {
        const data = await getStatus(last);
        if (data.status === 'completed') {
          setJobId(last);
          grafoRevealedRef.current = true;
          setAnalysis(data);
          setReports(data.result?.reports || {});
        } else {
          localStorage.removeItem('dobra_last_job');
        }
      } catch {
        localStorage.removeItem('dobra_last_job');
      }
    })();
  }, [accessCheck.hasAccess]);

  const reopenAnalysis = async (jid) => {
    try {
      setReports({}); setNicheLoading({}); setLoadingIndex(null); setPanel(null); setAnalyzing(false);
      setJobError(null); expandingRef.current.clear(); grafoRevealedRef.current = true;
      setJobId(jid);
      const data = await getStatus(jid);
      setAnalysis(data);
      setReports(data.result?.reports || {});   // hidrata reports já gerados (eager)
      localStorage.setItem('dobra_last_job', jid);
    } catch {
      // A análise pode ter sido substituída/removida (ex.: regenerar dedup-ou o
      // canal). Limpa o id morto e volta pro início (hero) com aviso, sem travar.
      setJobId(null); grafoRevealedRef.current = false;
      if (localStorage.getItem('dobra_last_job') === jid) localStorage.removeItem('dobra_last_job');
      toast.error(t('dobraNicho.analysis_not_found'));
    }
  };

  // Deep-link da página de análises: /dobra-nicho?a=<id> abre aquela análise.
  useEffect(() => {
    const a = searchParams.get('a');
    // Reabre só se o ?a= for um id DIFERENTE do já aberto (deep-link externo, ex.:
    // vindo da página de análises). NÃO apaga o ?a= — ele reflete a análise aberta
    // (breadcrumb "› Análise" + refresh reabre a mesma). O sync abaixo cuida dele.
    if (!a || !accessCheck.hasAccess || a === currentJobIdRef.current) return;
    reopenAnalysis(a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessCheck.hasAccess, searchParams]);

  // ── Polling do job ────────────────────────────────────────────────────────
  // Backstop de 26 min (logo ACIMA do STALE_MINUTES=25 do backend, que já marca
  // job preso como failed) + corte após N erros de rede seguidos. Assim o front
  // não desiste antes do backend resolver. Falha vira erro PERSISTENTE (card com
  // "tentar de novo"), não só um toast efêmero.
  useEffect(() => {
    if (!jobId || !analyzing) return undefined;
    const deadline = Date.now() + 26 * 60 * 1000;
    let failures = 0;
    const fail = (msg, retryable = true) => {
      setAnalyzing(false);
      // Limpa o grafo (parcial) já revelado: uma falha TARDIA (ex.: a Dobra voltou
      // vazia) não pode deixar na tela um grafo meio-pronto sem a Dobra — mostra o
      // card de erro com "tentar de novo".
      setAnalysis(null);
      setReports({});
      grafoRevealedRef.current = false;
      localStorage.removeItem('dobra_active_job');
      setJobErrorRetryable(retryable);
      setJobError(msg);
    };
    const tick = async () => {
      if (Date.now() > deadline) { fail(t('dobraNicho.timeout')); return; }
      try {
        const data = await getStatus(jobId);
        failures = 0;
        setProgress(data.progress || 0);
        setProgressMessage(data.message || '');
        // MODAL HÍBRIDO: assim que a grafo fica pronta (mesmo 'running'), revela
        // o grafo+DNA UMA vez e fecha o modal; os reports dos blocos do Prompt 2
        // vão preenchendo a cada poll (dobra/subnicho expandem sozinhos).
        if (data.result?.oportunidades?.length) {
          if (!grafoRevealedRef.current) { grafoRevealedRef.current = true; setAnalysis(data); }
          setReports(data.result.reports || {});
        }
        if (data.status === 'completed') {
          if (!grafoRevealedRef.current) { grafoRevealedRef.current = true; setAnalysis(data); }
          setReports(data.result?.reports || {});
          setNicheLoading({});
          setAnalyzing(false);
          localStorage.removeItem('dobra_active_job');
          localStorage.setItem('dobra_last_job', jobId);
          refreshHistory();
        } else if (data.status === 'failed') {
          // Dados insuficientes (pouca tração): falha DEFINITIVA -> mensagem direta
          // e SEM "tentar de novo" (repetir não muda). Demais falhas: retry normal.
          if (data.error === 'insufficient_data') fail(t('dobraNicho.error_insufficient_data'), false);
          else fail(data.message || t('dobraNicho.error_generic'), true);
        }
      } catch {
        failures += 1;
        if (failures >= 5) fail(t('dobraNicho.error_generic'));
      }
    };
    const iv = setInterval(tick, 2000);
    return () => clearInterval(iv);
  }, [jobId, analyzing, t]);

  // COMPLETAR-EM-2º-PLANO: depois de CONCLUÍDA, se a análise ainda está _incompleta
  // (algum bloco falhou e o backend está auto-completando), faz um poll LENTO que
  // preenche os blocos faltantes sozinho — sem o usuário precisar clicar. Para quando
  // tudo completa (ou após ~4 min, teto do auto-retry).
  useEffect(() => {
    if (!jobId || analyzing) return undefined;
    if (!analysis?.result?._incompleto) return undefined;
    const cap = jobId;
    let stop = false;
    const iv = setInterval(async () => {
      try {
        const data = await getStatus(cap);
        if (stop || currentJobIdRef.current !== cap) return;
        // A cada tick atualiza SÓ os reports (preenche os títulos que chegaram). NÃO
        // troca o objeto `analysis` aqui: isso mudaria a referência de `oportunidades`
        // e faria o grafo resetar (re-fit + perda de posições arrastadas) a cada 5s.
        if (data.result) setReports(data.result.reports || {});
        if (data.status !== 'completed' || !data.result?._incompleto) {
          setAnalysis(data);   // settle final: atualiza _incompleto -> some o indicador
          stop = true; clearInterval(iv);
        }
      } catch { /* tenta no próximo tick */ }
    }, 5000);
    const tout = setTimeout(() => { stop = true; clearInterval(iv); }, 4 * 60 * 1000);
    return () => { stop = true; clearInterval(iv); clearTimeout(tout); };
  }, [jobId, analyzing, analysis?.result?._incompleto]);

  // input pode vir do campo OU de um card da comunidade (channel_id).
  const handleAnalyze = async (force = false, inputOverride = null) => {
    const input = (inputOverride ?? channelInput).trim();
    if (!input) { toast.error(t('dobraNicho.enter_channel')); return; }
    if (inputOverride) setChannelInput(inputOverride);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      // IMPORTANTE: não limpa a análise aberta ANTES de o backend aceitar. Assim,
      // uma reanálise BLOQUEADA (ex.: RECENTLY_ANALYZED / INSUFFICIENT_VIEWS, que
      // são 4xx pré-job) só mostra um toast e o usuário CONTINUA vendo a análise
      // atual, em vez de ser jogado de volta pra tela inicial.
      const res = await startAnalysis(input, force, userLang);
      setAnalysis(null);
      setReports({});
      setNicheLoading({});
      setLoadingIndex(null);
      setPanel(null);
      setJobError(null);
      expandingRef.current.clear();
      grafoRevealedRef.current = false;
      setProgress(0);
      setProgressMessage(t('dobraNicho.starting'));
      setJobId(res.job_id);
      if (res.status === 'completed') {
        // CACHE da comunidade (já estava pronta): mesmo assim TOCA a motion de
        // busca (~2.4s) pra não dar sensação de cache; depois revela.
        const data = await getStatus(res.job_id);
        setAnalyzing(true);
        for (const [p, k] of [[12, 'step_fetch'], [42, 'step_pattern'], [72, 'step_map'], [98, 'step_map']]) {
          setProgress(p); setProgressMessage(t(`dobraNicho.${k}`)); await sleep(560);
        }
        grafoRevealedRef.current = true;
        setAnalysis(data);
        setReports(data.result?.reports || {});
        setAnalyzing(false);
        localStorage.setItem('dobra_last_job', res.job_id);
        refreshHistory();
      } else {
        // Persiste o job pra retomar se o usuário sair e voltar (roda em 2º plano).
        localStorage.setItem('dobra_active_job', res.job_id);
        setAnalyzing(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = (detail && typeof detail === 'object') ? detail.error_code : null;
      if (code === 'DOBRA_PRO_ONLY') setProModal(true); // gerar é exclusivo do Pro -> modal
      else if (code === 'INSUFFICIENT_VIEWS') toast.error(t('dobraNicho.insufficient_views'));
      else if (code === 'RECENTLY_ANALYZED') toast.error(t('dobraNicho.recently_analyzed', { days: detail?.min_days ?? 14, left: detail?.days_left ?? 1 }));
      else if (err?.response?.status === 429) toast.error(t('dobraNicho.limit_reached'));
      else if (err?.response?.status === 403) toast.error(t('dobraNicho.no_access'));
      else toast.error(typeof detail === 'string' ? detail : t('dobraNicho.error_start'));
    }
  };

  const oportunidades = analysis?.result?.oportunidades || [];
  // Análise VÁLIDA = tem oportunidades. Uma análise restaurada/aberta sem
  // oportunidades (grafo vazio) NÃO deve esconder o hero nem deixar a página em
  // branco — trata como "sem análise" e mostra o hero.
  const hasResult = oportunidades.length > 0;
  const channelTitle = analysis?.channel_title || analysis?.result?.dossier?.nome || '';

  // Reflete a análise aberta na URL (?a=<jobId>): dá o 3º nível do breadcrumb
  // ("Tendências › Dobra de Nicho › Análise") e faz o refresh reabrir a mesma
  // análise. Limpa o ?a= ao voltar pro hero. replace = não polui o histórico.
  useEffect(() => {
    if (!accessCheck.hasAccess) return;
    const cur = searchParams.get('a');
    if (hasResult && jobId) {
      if (cur !== jobId) setSearchParams((p) => { const n = new URLSearchParams(p); n.set('a', jobId); return n; }, { replace: true });
    } else if (cur) {
      setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('a'); return n; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResult, jobId, accessCheck.hasAccess]);

  // Se o ?a= for REMOVIDO da URL com uma análise ainda aberta (ex.: clique no
  // breadcrumb "Dobra de Nicho"), volta pro HERO. Depende de searchParams: dispara
  // exatamente quando a navegação tira o ?a=.
  useEffect(() => {
    if (!accessCheck.hasAccess) return;
    if (!searchParams.get('a') && hasResult) newAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const channelThumb = analysis?.result?.dossier?.thumbnail || '';

  // Report VAZIO = a geração daquele bloco falhou (caiu no fallback vazio). Lançador
  // (dobra) vazio = sem nichos; demais = sem títulos. Clicar num bloco vazio REGENERA
  // (o backend trata cache vazio como miss) em vez de só mostrar um painel vazio.
  const isEmptyReport = (rep, isLauncher) => {
    if (!rep) return true;
    if (isLauncher) return !((rep.nichos_sugeridos || []).length);
    return !((rep.titulos_prontos || []).length);
  };

  // Busca/regenera os títulos de UM nicho-alvo e mescla no report. Reusado pela
  // expansão do lançador (4 em paralelo) e pelo clique de regenerar um nicho vazio.
  const runNicheFetch = (cap, index, j) => {
    setNicheLoading((nl) => ({ ...nl, [`${index}.${j}`]: true }));
    return expandNiche(cap, index, j)
      .then((nres) => {
        if (currentJobIdRef.current !== cap) return;
        setReports((r) => {
          const rep = r[index];
          if (!rep) return r;
          const ns = [...(rep.nichos_sugeridos || [])];
          if (!ns[j]) return r;
          ns[j] = { ...ns[j], titulos: nres.titulos || [], mapa_de_equivalencia: nres.mapa_de_equivalencia || [], ideia_tema: nres.ideia_tema || ns[j].ideia_tema, recorte_alvo: nres.recorte_alvo || ns[j].recorte_alvo, como_aplicar: nres.como_aplicar || ns[j].como_aplicar };
          return { ...r, [index]: { ...rep, nichos_sugeridos: ns } };
        });
      })
      .catch(() => {})
      .finally(() => {
        if (currentJobIdRef.current !== cap) return;
        setNicheLoading((nl) => { const n = { ...nl }; delete n[`${index}.${j}`]; return n; });
      });
  };

  const handleOpportunityClick = async (index) => {
    const op = oportunidades[index];
    if (!op) return;
    scrollGraphIntoView();   // rola até o grafo (deixa as bolas visíveis)
    const isLauncher = op.tipo === 'dobra' || op.tipo === 'mar_oportunidades';
    if (reports[index] && !isEmptyReport(reports[index], isLauncher)) {
      // Lançador COM filhos já expandido no canvas — não reabre painel; lançador
      // SEM filhos (frame sem sugestões) abre o painel pra mostrar o conceito.
      const hasNiches = (reports[index].nichos_sugeridos || []).length > 0;
      if (!isLauncher || !hasNiches) setPanel({ kind: 'opportunity', oppIndex: index });
      return;
    }
    // reports[index] AUSENTE ou VAZIO (geração falhou) -> cai no fluxo de expand
    // abaixo e REGENERA o bloco (a não ser que a geração eager ainda esteja rodando).
    // Geração eager ainda rodando: não dispara expand lazy (o bloco está vindo) —
    // avisa que o sistema ainda está analisando.
    if (analyzing) { toast(t('dobraNicho.still_analyzing'), { icon: '⏳' }); return; }
    // Guarda SÍNCRONA contra clique duplo / re-render (state é assíncrono).
    if (expandingRef.current.has(index)) return;
    if (loadingIndex !== null) {
      if (loadingIndex !== index) toast(t('dobraNicho.wait_current'), { icon: '⏳' });
      return;
    }
    const capturedJobId = jobId;          // guard contra troca de análise no meio
    expandingRef.current.add(index);
    setLoadingIndex(index);
    try {
      const res = await expandOpportunity(jobId, index);
      if (currentJobIdRef.current !== capturedJobId) return; // análise mudou
      const report = res.report;
      setReports((r) => ({ ...r, [index]: report }));

      if (isLauncher) {
        const niches = report.nichos_sugeridos || [];
        if (!niches.length) {
          // lançador sem filhos: não vira nó morto — abre o painel com o conceito.
          setPanel({ kind: 'opportunity', oppIndex: index });
        }
        // Frame chegou: o grafo já mostra os 4 nós de nicho (sem títulos).
        // Agora busca os títulos de cada nicho EM PARALELO — cada um preenche
        // o respectivo nó assim que chega (renderização progressiva).
        niches.forEach((_, j) => runNicheFetch(capturedJobId, index, j));
      } else {
        setPanel({ kind: 'opportunity', oppIndex: index });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 404) {
        // A análise sumiu (substituída/removida no meio) — não fica clicando num
        // grafo morto: volta pro início com aviso.
        setJobId(null); setAnalysis(null); grafoRevealedRef.current = false;
        if (localStorage.getItem('dobra_last_job') === jobId) localStorage.removeItem('dobra_last_job');
        toast.error(t('dobraNicho.analysis_not_found'));
      } else {
        toast.error(typeof detail === 'string' ? detail : t('dobraNicho.error_expand'));
      }
    } finally {
      expandingRef.current.delete(index);
      setLoadingIndex(null);
    }
  };

  const handleNicheClick = (oppIndex, nicheIndex) => {
    scrollGraphIntoView();   // rola até o grafo (deixa as bolas visíveis)
    setPanel({ kind: 'niche', oppIndex, nicheIndex });
    // Nicho que veio SEM títulos (geração falhou) e parado -> regenera no clique.
    const niche = reports[oppIndex]?.nichos_sugeridos?.[nicheIndex];
    const key = `${oppIndex}.${nicheIndex}`;
    if (jobId && !analyzing && !nicheLoading[key] && niche && !((niche.titulos || []).length)) {
      runNicheFetch(jobId, oppIndex, nicheIndex);
    }
  };

  // Clicar no canal (centro) abre a análise profunda do DNA no mesmo drawer.
  const handleCenterClick = () => { scrollGraphIntoView(); setPanel({ kind: 'dna' }); };

  // "Analisar outro canal": volta pra tela inicial (hero + input). A análise
  // atual continua salva no histórico.
  const newAnalysis = () => {
    setAnalysis(null); setReports({}); setNicheLoading({}); setLoadingIndex(null); setPanel(null);
    setJobError(null); setChannelInput(''); expandingRef.current.clear();
    grafoRevealedRef.current = false;
    localStorage.removeItem('dobra_last_job');
  };

  // SUPER ADMIN: apaga QUALQUER análise (hard-delete no backend) — da galeria ou
  // da análise aberta. Usuário comum só usa soft-delete da própria (página de histórico).
  const handleDeleteAnalysis = async (id, e) => {
    if (e) e.stopPropagation();
    if (!id || !window.confirm(t('dobraNicho.confirm_delete_admin'))) return;
    try {
      await hideAnalysis(id);
      toast.success(t('dobraNicho.deleted'));
      setHistory((h) => h.filter((x) => x.id !== id));   // update otimista em "Minhas"
      if (analysis && jobId === id) newAnalysis();
      // Refresca a lista "Minhas" (sem comunidade).
      listAnalyses().then((rows) => setHistory(rows.map((r) => ({ ...r, id: r.job_id })))).catch(() => {});
    } catch {
      toast.error(t('dobraNicho.error_delete'));
    }
  };

  // REANALISAR o canal aberto (force_refresh): regera do ZERO, ignorando o cache da
  // comunidade. Disponível pra quem PODE gerar (pro/plus/super admin). O backend
  // bloqueia se a última análise tem < 14 dias (sem dados novos) — exceto super
  // admin. Substitui a anterior do usuário (1 por canal), não duplica.
  const reanalyzeCurrentAnalysis = () => {
    const cid = analysis?.channel_id;
    if (cid) handleAnalyze(true, cid);
  };

  // Botão de histórico -> página dedicada de análises (todos os canais salvos).
  const historyMenu = () => (history.length === 0 ? null : (
    <button
      onClick={() => navigate('/dobra-nicho/historico')}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, padding: '7px 11px', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)', fontSize: 12.5, transition: 'border-color 0.15s, color 0.15s' }}
    >
      <History size={13} />{t('dobraNicho.history')}
    </button>
  ));

  // ── Renderização ──────────────────────────────────────────────────────────
  if (accessCheck.loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
        <Header />
        <main className="dp-main-content" style={{ marginLeft: isMobile ? 0 : `${sidebarWidth}px`, marginTop: 60, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader2 size={40} className="animate-spin" style={{ color: 'var(--dp-blue-500)' }} />
        </main>
      </div>
    );
  }

  if (!accessCheck.hasAccess) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
        <Header />
        <FeatureUpsellPage
          icon={<Network size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
          title={t('dobraNicho.title')}
          subtitle={t('dobraNicho.subtitle')}
          upsell={{
            badge: t('dobraNicho.upsell_badge'),
            headline: t('dobraNicho.upsell_headline'),
            subhead: t('dobraNicho.upsell_subhead'),
            bullets: [
              { Icon: Network, text: t('dobraNicho.upsell_bullet_1') },
              { Icon: Search, text: t('dobraNicho.upsell_bullet_2') },
              { Icon: ArrowRight, text: t('dobraNicho.upsell_bullet_3') },
            ],
            ctaLabel: t('dobraNicho.upsell_cta'),
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
      <Header />
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        // Painel aberto (desktop): abre espaço à direita pro drawer. SEM painel,
        // NÃO sobrescreve o padding-right (undefined) — usa o padding padrão do
        // .dp-main-content (mesma margem das outras páginas; antes 0 fazia os cards
        // ultrapassarem o container à direita).
        paddingRight: (!isMobile && panel) ? 'min(560px, 96vw)' : undefined,
        transition: 'margin-left 0.3s ease, padding-right 0.3s ease',
        marginTop: 60, flex: 1, minWidth: 0, animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        <PageHeader
          icon={<Network size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title={t('dobraNicho.title')}
          description={t('dobraNicho.subtitle')}
        />

        {/* ── HERO inicial: o que a feature faz + entrada do canal ─────────── */}
        {!hasResult && !analyzing && !jobError && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            style={{ padding: '4px 0 8px' }}
          >
            {/* Intro centralizada: ícone-destaque (gradiente) + título + subtítulo + entrada */}
            <div style={{ maxWidth: 720, margin: '0 auto 44px', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)', boxShadow: '0 8px 24px rgba(13,174,255,0.30)' }}>
                <Network size={30} color="#fff" strokeWidth={2.2} />
              </div>
              <h1 style={{ fontSize: 30, fontWeight: 800, color: 'var(--dp-text-primary)', margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.5px', fontFamily: 'var(--dp-font)' }}>{t('dobraNicho.hero_title')}</h1>
              <p style={{ fontSize: 15, color: 'var(--dp-text-secondary)', maxWidth: 620, margin: '0 auto 24px', lineHeight: 1.6, fontFamily: 'var(--dp-font)' }}>{t('dobraNicho.hero_subtitle')}</p>

              <div style={{ maxWidth: 580, margin: '0 auto', display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                <input
                  type="text"
                  value={channelInput}
                  onChange={(e) => setChannelInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAnalyze(false); }}
                  placeholder={t('dobraNicho.input_placeholder')}
                  style={{ flex: '1 1 280px', minWidth: 220, padding: '12px 15px', borderRadius: 10, border: '1px solid var(--dp-border-medium)', background: 'var(--dp-bg-card)', color: 'var(--dp-text-primary)', fontSize: 14.5, fontFamily: 'var(--dp-font)', outline: 'none' }}
                />
                <button onClick={() => handleAnalyze(false)} className="dp-btn-cta">
                  <Search size={15} />{t('dobraNicho.analyze')}<ArrowRight size={15} />
                </button>
              </div>
              {history.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>{historyMenu()}</div>
              )}
            </div>

            {/* Como funciona: DNA do canal + os 3 motores de oportunidade (largura total) */}
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--dp-text-tertiary)', marginBottom: 12, fontFamily: 'var(--dp-font)' }}>{t('dobraNicho.how_it_works')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16, marginBottom: 44 }}>
              {[
                { Icon: Sparkles, key: 'dna' },
                { Icon: Lightbulb, key: 'mar_ideias' },
                { Icon: Network, key: 'dobra' },
                { Icon: Globe, key: 'adapt' },
              ].map(({ Icon, key }) => (
                <div
                  key={key}
                  style={{ background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 14, padding: 18 }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(13,174,255,0.18), rgba(13,174,255,0.05))', border: '1px solid rgba(13,174,255,0.20)', marginBottom: 12 }}>
                    <Icon size={19} style={{ color: '#0DAEFF' }} />
                  </div>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--dp-text-primary)', marginBottom: 5 }}>{t(`dobraNicho.card_${key}_title`)}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--dp-text-secondary)', lineHeight: 1.5 }}>{t(`dobraNicho.card_${key}_desc`)}</div>
                </div>
              ))}
            </div>

            {/* "Minhas análises" — TODAS as análises do usuário (geradas OU recuperadas
                do cache). Sem aba Comunidade. Busca client-side sobre o histórico. */}
            {history.length > 0 && (
              <div style={{ textAlign: 'left' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 22 }}>
                  <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 0, maxWidth: 480 }}>
                    <Search size={15} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-secondary)', pointerEvents: 'none' }} />
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('dobraNicho.search_mine_placeholder')}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '10px 36px', borderRadius: 10, background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', fontSize: 13.5, outline: 'none', transition: 'border-color 0.15s' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} aria-label="limpar" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', background: 'transparent', border: 'none', color: 'var(--dp-text-secondary)', cursor: 'pointer', padding: 4 }}>
                        <X size={15} />
                      </button>
                    )}
                  </div>
                </div>

                {(() => {
                  const q = searchQuery.trim();
                  const delProp = (h) => (isSuperAdmin ? (e) => handleDeleteAnalysis(h.id, e) : null);
                  const list = q
                    ? history.filter((h) => `${h.channel_title || ''} ${h.nicho_principal || ''} ${h.nicho || ''} ${h.channel_id || ''}`.toLowerCase().includes(q.toLowerCase()))
                    : history;
                  return (
                    <>
                      <SectionHeading count={list.length || null}>{t('dobraNicho.view_mine')}</SectionHeading>
                      {list.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                          {list.map((h) => (
                            <CommunityCard key={h.id} h={{ ...h, nicho_principal: h.nicho_principal || h.nicho }} t={t} onClick={() => reopenAnalysis(h.id)} onDelete={delProp(h)} />
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: 'var(--dp-text-secondary)', fontSize: 13, padding: '8px 0' }}>{q ? t('dobraNicho.search_no_results', { q }) : t('dobraNicho.mine_empty')}</div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </motion.div>
        )}

        {/* ── Analisando: modal por etapas — só ATÉ a grafo ficar pronta ────── */}
        {analyzing && !(analysis?.result?.oportunidades?.length) && (
          <AnalyzingModal progress={progress} message={progressMessage} t={t} />
        )}

        {/* ── Falha persistente ─────────────────────────────────────────────── */}
        {!analyzing && !hasResult && jobError && (
          <div style={{ background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 12, padding: '40px 24px', textAlign: 'center', maxWidth: 460, margin: '24px auto' }}>
            <AlertCircle size={32} style={{ color: 'var(--dp-red-500)', margin: '0 auto 12px' }} />
            <div style={{ color: 'var(--dp-text-primary)', fontWeight: 600, marginBottom: 16 }}>{jobError}</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              {jobErrorRetryable && (
                <button onClick={() => handleAnalyze(false)} className="dp-btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                  <RefreshCw size={14} />{t('dobraNicho.retry')}
                </button>
              )}
              <button onClick={newAnalysis} className="dp-btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <Plus size={14} />{t('dobraNicho.new_analysis')}
              </button>
            </div>
          </div>
        )}

        {/* ── Análise pronta (ou revelando): pré-análise (com ações) + grafo ─── */}
        {hasResult && (
          <>
            <DnaHeader
              analysis={analysis}
              reports={reports}
              t={t}
              analyzing={analyzing}
              progress={progress}
              progressMessage={progressMessage}
              hasHistory={history.length > 0}
              onNewAnalysis={newAnalysis}
              onBack={newAnalysis}
              onOpenHistory={() => navigate('/dobra-nicho/historico')}
              isSuperAdmin={isSuperAdmin}
              canGenerate={accessCheck.canGenerate}
              onDelete={() => handleDeleteAnalysis(jobId)}
              onReanalyze={reanalyzeCurrentAnalysis}
            />

            {/* Instrução "como aplicar" — RECOLHIDA por padrão pra não tampar o grafo
                (o dono pediu o grafo mais visível). Clica no cabeçalho pra abrir. */}
            <div style={{ background: 'linear-gradient(135deg, rgba(13,174,255,0.06), var(--dp-bg-primary))', border: '1px solid var(--dp-border-subtle)', borderRadius: 12, marginBottom: 14 }}>
              <button
                onClick={() => setHowtoOpen((o) => !o)}
                style={{ width: '100%', display: 'flex', gap: 10, alignItems: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: '11px 15px', textAlign: 'left', fontFamily: 'var(--dp-font)' }}
              >
                <Lightbulb size={17} style={{ color: 'var(--dp-blue-500)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: 'var(--dp-text-primary)' }}>{t('dobraNicho.howto_title')}</span>
                <ChevronDown size={16} style={{ color: 'var(--dp-text-secondary)', flexShrink: 0, transform: howtoOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {howtoOpen && (
                <div style={{ padding: '0 15px 13px 42px' }}>
                  <div style={{ fontSize: 12.5, color: 'var(--dp-text-secondary)', lineHeight: 1.5, marginBottom: 6 }}>{t('dobraNicho.howto_intro')}</div>
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--dp-text-secondary)', lineHeight: 1.55, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <li>{t('dobraNicho.howto_step1')}</li>
                    <li>{t('dobraNicho.howto_step2')}</li>
                    <li>{t('dobraNicho.howto_step3')}</li>
                  </ol>
                </div>
              )}
            </div>

            {oportunidades.length > 0 && (
              <div ref={graphWrapRef} style={{ scrollMarginTop: 72 }}>
                <NichoFoldGraph
                  channelTitle={channelTitle}
                  channelThumb={channelThumb}
                  oportunidades={oportunidades}
                  reports={reports}
                  nicheLoading={nicheLoading}
                  onOpportunityClick={handleOpportunityClick}
                  onNicheClick={handleNicheClick}
                  onCenterClick={handleCenterClick}
                  loadingIndex={loadingIndex}
                  isMobile={isMobile}
                  /* muda ao abrir/trocar/fechar o painel -> reenquadra as bolas */
                  fitSignal={panel ? `${panel.kind}:${panel.oppIndex ?? ''}:${panel.nicheIndex ?? ''}` : 'closed'}
                />
                <p style={{ color: 'var(--dp-text-secondary)', fontSize: 13, marginTop: 10 }}>
                  {t('dobraNicho.hint')}
                </p>
              </div>
            )}
          </>
        )}
      </main>

      {panel && (
        <DetailPanel
          panel={panel}
          analysis={analysis}
          oportunidades={oportunidades}
          reports={reports}
          nicheLoading={nicheLoading}
          analyzing={analyzing}
          onClose={() => setPanel(null)}
          t={t}
        />
      )}

      {proModal && (
        <ProOnlyModal t={t} onClose={() => setProModal(false)} onUpgrade={() => { setProModal(false); navigate('/meu-plano'); }} />
      )}
    </div>
  );
}

// ── Modal "Gerar é exclusivo do Pro" — basic/lite veem a comunidade, mas só
// pro/plus geram análises novas. CTA leva pro /meu-plano. ───────────────────
function ProOnlyModal({ t, onClose, onUpgrade }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      role="dialog" aria-modal="true" aria-label={t('dobraNicho.pro_only_title')}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'dp-pageFadeIn 0.2s ease' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(440px, 96vw)', background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 16, padding: '30px 28px', boxShadow: '0 24px 64px rgba(0,0,0,0.5)', fontFamily: 'var(--dp-font)', textAlign: 'center' }}
      >
        <div style={{ width: 52, height: 52, borderRadius: 14, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(13,174,255,0.18), rgba(13,174,255,0.06))', border: '1px solid rgba(13,174,255,0.3)' }}>
          <Sparkles size={24} style={{ color: 'var(--dp-blue-500)' }} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>{t('dobraNicho.pro_only_badge')}</div>
        <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 700, color: 'var(--dp-text-primary)', lineHeight: 1.25 }}>{t('dobraNicho.pro_only_title')}</h2>
        <p style={{ margin: '0 0 22px', fontSize: 14, color: 'var(--dp-text-secondary)', lineHeight: 1.55 }}>{t('dobraNicho.pro_only_body')}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={onClose} className="dp-btn-secondary" style={{ padding: '10px 18px' }}>{t('dobraNicho.pro_only_dismiss')}</button>
          <button onClick={onUpgrade} className="dp-btn-primary" style={{ padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Sparkles size={15} />{t('dobraNicho.pro_only_cta')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal "Analisando…" com progresso POR ETAPAS (motion) ────────────────────
const ANALYZE_STEPS = [
  { key: 'fetch', at: 10 },
  { key: 'pattern', at: 40 },
  { key: 'map', at: 45 },
];
function AnalyzingModal({ progress, message, t }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
      style={{ maxWidth: 460, margin: '56px auto', background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 16, padding: '32px 30px', fontFamily: 'var(--dp-font)' }}
    >
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <div style={{ color: 'var(--dp-text-primary)', fontWeight: 800, fontSize: 17, marginBottom: 4 }}>{t('dobraNicho.analyzing_title')}</div>
        <div style={{ color: 'var(--dp-text-secondary)', fontSize: 13, minHeight: 18 }}>{message || t('dobraNicho.starting')}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {ANALYZE_STEPS.map((s, i) => {
          const next = ANALYZE_STEPS[i + 1];
          const done = next ? progress >= next.at : progress >= 100;
          const active = !done && progress >= (i === 0 ? 0 : s.at);
          const color = done ? '#34D399' : active ? 'var(--dp-blue-500)' : 'var(--dp-text-tertiary)';
          return (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0' }}>
              <div style={{ position: 'relative', width: 26, height: 26, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {done ? (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 18 }} style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(52,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Check size={15} style={{ color: '#34D399' }} />
                  </motion.div>
                ) : active ? (
                  <Loader2 size={20} className="animate-spin" style={{ color: 'var(--dp-blue-500)' }} />
                ) : (
                  <div style={{ width: 9, height: 9, borderRadius: '50%', border: '2px solid var(--dp-border-medium)' }} />
                )}
                {i < ANALYZE_STEPS.length - 1 && <div style={{ position: 'absolute', top: 26, left: '50%', transform: 'translateX(-50%)', width: 2, height: 16, background: done ? '#34D399' : 'var(--dp-border-subtle)' }} />}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: active ? 700 : 600, color: active || done ? 'var(--dp-text-primary)' : 'var(--dp-text-secondary)' }}>{t(`dobraNicho.step_${s.key}`)}</div>
            </div>
          );
        })}
      </div>
      <div style={{ height: 6, background: 'var(--dp-border-subtle)', borderRadius: 999, overflow: 'hidden', marginTop: 20 }}>
        <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #0DAEFF, #06B6D4)', transition: 'width 0.4s ease' }} />
      </div>
    </motion.div>
  );
}

// Card de canal (galeria da comunidade + busca + minhas análises) — clicar
// abre/dispara a análise. onDelete (só super admin) mostra a lixeira pra apagar.
const CommunityCard = React.memo(function CommunityCard({ h, onClick, onDelete, t }) {
  return (
    <div
      onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.zIndex = '2'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.35)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.zIndex = '1'; e.currentTarget.style.boxShadow = 'none'; }}
      style={{ position: 'relative', zIndex: 1, flexShrink: 0, width: 212, background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 12, padding: 12, cursor: 'pointer', transition: 'transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease' }}
    >
      {onDelete && (
        <button
          onClick={onDelete}
          title={t ? t('dobraNicho.delete_analysis') : 'Apagar'}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-red-500)'; e.currentTarget.style.borderColor = 'var(--dp-red-500)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
          style={{ position: 'absolute', top: 7, right: 7, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 7, background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', color: 'var(--dp-text-secondary)', cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s' }}
        >
          <Trash2 size={12} />
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: h.nicho_principal ? 8 : 0, paddingRight: onDelete ? 26 : 0 }}>
        {h.thumbnail ? (
          <img src={h.thumbnail} alt="" referrerPolicy="no-referrer" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--dp-border-subtle)' }} />
        ) : (
          <div style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,174,255,0.12)', color: 'var(--dp-blue-500)', fontWeight: 800, fontSize: 16 }}>{(h.channel_title || '?').trim().charAt(0).toUpperCase()}</div>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.channel_title || h.channel_id}</div>
        </div>
      </div>
      {h.nicho_principal && (
        <div style={{ fontSize: 11.5, color: 'var(--dp-text-secondary)', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{h.nicho_principal}</div>
      )}
      {h.created_at && formatRelativeTime(h.created_at, t) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 10.5, color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
          <CalendarClock size={11} style={{ flexShrink: 0 }} />
          {t('dobraNicho.card_updated', { rel: formatRelativeTime(h.created_at, t) })}
        </div>
      )}
    </div>
  );
});

// Carrossel estilo Netflix: 2 LINHAS fixas, rolagem HORIZONTAL (arrastar pro lado).
// Os cards fluem em colunas (preenchem as 2 linhas e seguem pro lado). Drag com o
// mouse move o scroll; um drag de verdade cancela o clique do card (não abre análise
// sem querer). Touch/trackpad já rolam nativamente.
function CardCarousel({ children, onNearEnd }) {
  const ref = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const onDown = (e) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
  };
  const onMove = (e) => {
    const el = ref.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  };
  const end = () => { drag.current.down = false; };
  // Infinite scroll horizontal: ao chegar perto do fim (≈1,5 card), pede a próxima
  // página. O pai segura contra chamadas repetidas (flag de loading). Setar scrollLeft
  // no drag também dispara onScroll, então cobre arrastar E rolar nativo.
  const onScroll = () => {
    const el = ref.current;
    if (!el || !onNearEnd) return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 360) onNearEnd();
  };
  return (
    <div
      ref={ref}
      onScroll={onScroll}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={end}
      onPointerLeave={end}
      onClickCapture={(e) => { if (drag.current.moved) { e.stopPropagation(); e.preventDefault(); drag.current.moved = false; } }}
      style={{
        display: 'grid', gridAutoFlow: 'column', gridTemplateRows: 'repeat(2, auto)',
        gridAutoColumns: 'max-content', gap: 12, overflowX: 'auto', overflowY: 'hidden',
        // paddingTop/Bottom dão folga pro "lift" do hover (translateY -3px) não ser
        // cortado pelo overflowY:hidden do carrossel (card "ficando atrás" no topo).
        paddingTop: 6, paddingBottom: 12, cursor: 'grab', scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch',
      }}
    >
      {children}
    </div>
  );
}

// Título de seção (comunidade/minhas/busca) com contador em PÍLULA AZUL (gradiente
// dos botões do DarkPlanner). count omitido/null = sem pílula.
function SectionHeading({ children, count }) {
  return (
    <h3 style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '0 0 14px', fontSize: 18, fontWeight: 700, color: 'var(--dp-text-primary)', letterSpacing: '-0.3px', fontFamily: 'var(--dp-font)' }}>
      {children}
      {count != null && (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 24, height: 22, padding: '0 9px', borderRadius: 9999, fontSize: 12.5, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', boxShadow: '0 2px 8px rgba(13,174,255,0.30)' }}>
          {count}
        </span>
      )}
    </h3>
  );
}

// ── Cabeçalho de DNA (análise upfront do Prompt 1) ───────────────────────────
function fmtNum(n) {
  n = Number(n || 0);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace('.0', '')}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace('.0', '')}K`;
  return String(n);
}

// Data curta (dd/mm/aaaa) no locale do navegador — usada no card e no cabeçalho
// pra mostrar QUANDO a análise foi feita/atualizada.
function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Cor por nível de confiança.
const CONF_COLOR = {
  alta: '#34D399', media: '#F59E0B', baixa: '#F87171',
  exploratoria: '#0DAEFF', exploratorio_promissor: '#0DAEFF',
};

// Selo de confiança com tooltip de hover explicando o PORQUÊ (sempre dado real).
function ConfBadge({ level, lines, t }) {
  const [open, setOpen] = useState(false);
  if (!level) return null;
  const color = CONF_COLOR[level] || '#8AA0B2';
  const body = (lines || []).map((l) => (l == null ? '' : String(l).trim())).filter(Boolean);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, color, background: `${color}1A`, border: `1px solid ${color}55`, padding: '2px 8px', borderRadius: 999, cursor: 'help', whiteSpace: 'nowrap' }}>
        <Info size={11} />{t(`dobraNicho.confidence_${level}`, level)}
      </span>
      {open && body.length > 0 && (
        <span role="tooltip" style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 80, width: 280, maxWidth: '78vw', background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 10, padding: '10px 12px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', textAlign: 'left', whiteSpace: 'normal' }}>
          <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>{t('dobraNicho.confidence_tooltip_label')}</span>
          {body.map((ln, i) => (
            <span key={i} style={{ display: 'block', fontSize: 12, color: 'var(--dp-text-secondary)', lineHeight: 1.45, marginTop: i ? 4 : 0 }}>{ln}</span>
          ))}
        </span>
      )}
    </span>
  );
}

function DnaHeader({ analysis, reports, t, analyzing, progress, progressMessage, hasHistory, onNewAnalysis, onBack, onOpenHistory, isSuperAdmin, canGenerate, onDelete, onReanalyze }) {
  const res = analysis?.result || {};
  const pv = res.padrao_vencedor || {};
  const forma = res.forma_funcao?.forma || {};
  const anc = res.ancoragem || {};
  const pub = anc.publico || {};
  const d = res.dossier || {};
  const temas = (forma.temas || []).slice(0, 5);
  const nome = d.nome || analysis?.channel_title || '—';
  const nichoPrincipal = anc.nicho_principal || d.nicho_aparente;
  const publico = [pub.demografico, pub.psicografico].filter(Boolean).join(' · ');
  const stats = [
    { v: fmtNum(d.subscriber_count), l: t('dobraNicho.dna_subs') },
    { v: fmtNum(d.channel_views), l: t('dobraNicho.dna_views') },
    { v: fmtNum(d.video_count), l: t('dobraNicho.dna_published') },
    { v: d.sample_size ?? '—', l: t('dobraNicho.dna_videos') },
    { v: d.outliers_count ?? '—', l: t('dobraNicho.dna_outliers') },
    { v: d.top_outlier_ratio ? `${d.top_outlier_ratio}x` : '—', l: t('dobraNicho.dna_top_outlier') },
  ];
  // Confiança GERAL do canal + porquê (ancorado em dado real do dossiê).
  const confLevel = res.confianca;
  const confLines = [
    t(`dobraNicho.confidence_why_${confLevel}`, ''),
    pv.resumo,
    (d.outliers_count != null || d.sample_size != null)
      ? t('dobraNicho.confidence_basis', { out: d.outliers_count ?? 0, ratio: d.top_outlier_ratio ?? 0, n: d.sample_size ?? 0 })
      : '',
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      style={{
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(13,174,255,0.06), var(--dp-bg-card))',
        border: '1px solid var(--dp-border-subtle)', borderRadius: 14, padding: 18, marginBottom: 14,
        fontFamily: 'var(--dp-font)',
      }}
    >
      {/* Barra fina de progresso no topo do card enquanto gera os blocos. */}
      {analyzing && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--dp-border-subtle)', borderTopLeftRadius: 14, borderTopRightRadius: 14, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress || 0}%`, background: 'linear-gradient(90deg, #0DAEFF, #06B6D4)', transition: 'width 0.4s ease' }} />
        </div>
      )}

      {/* Identidade do canal + AÇÕES (analisar outro / histórico) ao lado */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flex: '1 1 320px', minWidth: 0 }}>
          {d.thumbnail ? (
            <img src={d.thumbnail} alt="" referrerPolicy="no-referrer" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--dp-border-subtle)' }} />
          ) : (
            <div style={{ width: 56, height: 56, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,174,255,0.12)', color: 'var(--dp-blue-500)', fontWeight: 800, fontSize: 22 }}>{(nome || '?').trim().charAt(0).toUpperCase()}</div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--dp-text-primary)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>{nome}</h3>
              {d.idioma && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--dp-text-secondary)', background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', padding: '1px 7px', borderRadius: 999, textTransform: 'uppercase' }}>{d.idioma.toUpperCase()}</span>}
              <ConfBadge level={confLevel} lines={confLines} t={t} />
            </div>
            {nichoPrincipal && <div style={{ fontSize: 12.5, color: 'var(--dp-text-primary)', lineHeight: 1.4 }}><span style={{ color: 'var(--dp-blue-500)', fontWeight: 600 }}>{t('dobraNicho.dna_main_niche')}:</span> {nichoPrincipal}</div>}
            {publico && <div style={{ fontSize: 12, color: 'var(--dp-text-secondary)', lineHeight: 1.4, marginTop: 2 }}><span style={{ fontWeight: 600 }}>{t('dobraNicho.dna_audience')}:</span> {publico}</div>}
            {formatRelativeTime(analysis?.completed_at || analysis?.created_at, t) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--dp-text-tertiary)', lineHeight: 1.4, marginTop: 4 }}>
                <CalendarClock size={11} style={{ flexShrink: 0 }} />
                {t('dobraNicho.card_updated', { rel: formatRelativeTime(analysis?.completed_at || analysis?.created_at, t) })}
              </div>
            )}
          </div>
        </div>
        {/* Ações compactas ao lado do canal (não ocupam uma linha só). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {analyzing && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: 'var(--dp-blue-500)' }}>
              <Loader2 size={13} className="animate-spin" />{progressMessage || ''}
            </span>
          )}
          {!analyzing && res._incompleto && (
            <span title={t('dobraNicho.completing_hint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, color: 'var(--dp-blue-500)' }}>
              <Loader2 size={13} className="animate-spin" />{t('dobraNicho.completing')}
            </span>
          )}
          {onBack && (
            <button
              onClick={onBack}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, padding: '6px 11px', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)', fontSize: 12, whiteSpace: 'nowrap', transition: 'border-color 0.15s, color 0.15s' }}
            >
              <ArrowLeft size={13} />{t('dobraNicho.back')}
            </button>
          )}
          {onNewAnalysis && (
            <button
              onClick={onNewAnalysis}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, padding: '6px 11px', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)', fontSize: 12, whiteSpace: 'nowrap', transition: 'border-color 0.15s, color 0.15s' }}
            >
              <Plus size={13} />{t('dobraNicho.new_analysis')}
            </button>
          )}
          {analysis?.result && (
            <>
              <button
                onClick={() => downloadTxt(analysis, reports, t)}
                title={t('dobraNicho.export_txt')}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, padding: '6px 11px', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)', fontSize: 12, whiteSpace: 'nowrap', transition: 'border-color 0.15s, color 0.15s' }}
              >
                <FileText size={13} />{t('dobraNicho.export_txt')}
              </button>
              <button
                onClick={() => downloadPdf(analysis, reports, t)}
                title={t('dobraNicho.export_pdf')}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, padding: '6px 11px', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)', fontSize: 12, whiteSpace: 'nowrap', transition: 'border-color 0.15s, color 0.15s' }}
              >
                <Download size={13} />{t('dobraNicho.export_pdf')}
              </button>
            </>
          )}
          {hasHistory && onOpenHistory && (
            <button
              onClick={onOpenHistory}
              title={t('dobraNicho.history')}
              aria-label={t('dobraNicho.history')}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, color: 'var(--dp-text-secondary)', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s' }}
            >
              <History size={14} />
            </button>
          )}
          {canGenerate && onReanalyze && !analyzing && (
            <button
              onClick={onReanalyze}
              title={t('dobraNicho.reanalyze_hint')}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, padding: '6px 11px', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)', fontSize: 12, whiteSpace: 'nowrap', transition: 'border-color 0.15s, color 0.15s' }}
            >
              <RefreshCw size={13} />{t('dobraNicho.reanalyze_channel')}
            </button>
          )}
          {isSuperAdmin && onDelete && (
            <button
              onClick={onDelete}
              title={t('dobraNicho.delete_analysis')}
              aria-label={t('dobraNicho.delete_analysis')}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-red-500)'; e.currentTarget.style.color = 'var(--dp-red-500)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, color: 'var(--dp-text-secondary)', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s' }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Métricas (linha própria, full width) */}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14 }}>
        {stats.map((s, i) => (
          <div key={i} style={{ minWidth: 48 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--dp-text-primary)' }}>{s.v}</div>
            <div style={{ fontSize: 10, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Padrão vencedor (resumo) + temas */}
      {(pv.resumo || temas.length > 0) && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--dp-border-subtle)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('dobraNicho.dna_winning_pattern')}</div>
          {pv.resumo && <div style={{ fontSize: 13.5, color: 'var(--dp-text-primary)', lineHeight: 1.5, marginBottom: temas.length ? 10 : 0 }}>{pv.resumo}</div>}
          {temas.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {temas.map((tm, i) => (
                <span key={i} style={{ fontSize: 10.5, color: 'var(--dp-text-secondary)', background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', padding: '2px 8px', borderRadius: 999 }}>{tm}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

// ── Painel lateral de detalhe (premium) ──────────────────────────────────────
function TitleCard({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }).catch(() => {});
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, background: 'var(--dp-bg-primary)',
      border: '1px solid var(--dp-border-subtle)', borderRadius: 10, padding: '11px 13px', marginBottom: 8,
    }}>
      <span style={{ flex: 1, fontSize: 14, color: 'var(--dp-text-primary)', lineHeight: 1.4 }}>{text}</span>
      <button
        onClick={copy}
        title="Copiar"
        onMouseEnter={(e) => { if (!copied) e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
        onMouseLeave={(e) => { if (!copied) e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0,
          color: copied ? '#34D399' : 'var(--dp-text-secondary)', display: 'flex', transition: 'color 0.15s',
        }}
      >{copied ? <Check size={16} /> : <Copy size={16} />}</button>
    </div>
  );
}

function PillList({ title, items, color }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: color || 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {items.map((it, i) => (
          <div key={i} style={{ fontSize: 13, color: 'var(--dp-text-primary)', lineHeight: 1.45, paddingLeft: 15, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0, top: 7, width: 5, height: 5, borderRadius: '50%', background: color || 'var(--dp-blue-500)' }} />
            {it}
          </div>
        ))}
      </div>
    </div>
  );
}

// Drawer da análise PROFUNDA do canal (clique no círculo central).
function DnaPanel({ analysis, onClose, closeRef, t }) {
  const res = analysis?.result || {};
  const anc = res.ancoragem || {};
  const pub = anc.publico || {};
  const pv = res.padrao_vencedor || {};
  const funcao = res.forma_funcao?.funcao || {};
  const ev = (pv.evidencias_de_performance || []).slice(0, 3);
  const heading = res.dossier?.nome || analysis?.channel_title || '—';
  const Row = ({ label, value }) => (value ? (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: 'var(--dp-text-primary)', lineHeight: 1.45 }}>{value}</div>
    </div>
  ) : null);
  return (
    <div role="dialog" aria-label={t('dobraNicho.dna_title')} style={{
      position: 'fixed', top: 60, right: 0, bottom: 0, width: 'min(560px, 96vw)', zIndex: 51,
      background: 'var(--dp-bg-card)', borderLeft: '1px solid var(--dp-border-subtle)',
      boxShadow: '-16px 0 48px rgba(0,0,0,0.45)', overflowY: 'auto', padding: '28px 30px',
      fontFamily: 'var(--dp-font)', animation: 'dp-slideInRight 0.25s ease',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('dobraNicho.dna_title')}</div>
          <h2 style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 700, color: 'var(--dp-text-primary)', lineHeight: 1.25 }}>{heading}</h2>
        </div>
        <button
          ref={closeRef}
          onClick={onClose}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-card-hover)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dp-bg-primary)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
          style={{ background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--dp-text-secondary)', flexShrink: 0, transition: 'background 0.15s, color 0.15s' }}
        ><X size={18} /></button>
      </div>

      <Row label={t('dobraNicho.dna_main_niche')} value={anc.nicho_principal} />
      <Row label={t('dobraNicho.dna_subniche')} value={anc.subnicho_atual} />
      <Row label={t('dobraNicho.dna_audience')} value={[pub.demografico, pub.psicografico].filter(Boolean).join(' · ')} />
      <Row label={t('dobraNicho.dna_territory')} value={anc.territorio} />

      {pv.resumo && (
        <div style={{ margin: '4px 0 18px', padding: '12px 14px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(13,174,255,0.06), var(--dp-bg-primary))', border: '1px solid var(--dp-border-subtle)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>{t('dobraNicho.dna_winning_pattern')}</div>
          <div style={{ fontSize: 13.5, color: 'var(--dp-text-primary)', lineHeight: 1.5 }}>{pv.resumo}</div>
        </div>
      )}

      {ev.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{t('dobraNicho.dna_evidence')}</div>
          {ev.map((e, i) => (
            <div key={i} style={{ background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: 'var(--dp-text-primary)', fontWeight: 600, lineHeight: 1.35 }}>{e.titulo}</div>
              <div style={{ fontSize: 11.5, color: 'var(--dp-text-secondary)', marginTop: 3 }}>{fmtNum(e.views)} views · {e.overperformance_ratio}x</div>
            </div>
          ))}
        </div>
      )}

      <PillList title={t('dobraNicho.dna_common')} items={pv.o_que_os_vencedores_tem_em_comum} color="#0DAEFF" />
      <PillList title={t('dobraNicho.dna_triggers')} items={pv.gatilhos_vencedores} color="#F59E0B" />
      <PillList title={t('dobraNicho.dna_formulas')} items={pv.formulas_de_titulo_vencedoras} color="#34D399" />
      <PillList title={t('dobraNicho.dna_function')} items={[funcao.promessa, funcao.emocao_dominante, funcao.fantasia_identidade].filter(Boolean)} color="#06B6D4" />
    </div>
  );
}

function DetailPanel({ panel, analysis, oportunidades, reports, nicheLoading, analyzing, onClose, t }) {
  const closeRef = useRef(null);
  // Fecha por Esc. NÃO rouba foco ao abrir (drawer não-bloqueante: o usuário
  // continua clicando nos círculos do grafo com o painel aberto).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Clique no CANAL (centro) -> análise profunda do DNA (drawer dedicado).
  if (panel.kind === 'dna') return <DnaPanel analysis={analysis} onClose={onClose} closeRef={closeRef} t={t} />;

  const op = oportunidades[panel.oppIndex];
  const report = reports[panel.oppIndex];
  if (!op || !report) return null;

  const isNiche = panel.kind === 'niche';
  const niche = isNiche ? (report.nichos_sugeridos || [])[panel.nicheIndex] : null;
  const titulos = isNiche ? (niche?.titulos || []) : (report.titulos_prontos || []);
  const mapa = isNiche ? (niche?.mapa_de_equivalencia || []) : (report.mapa_de_equivalencia || []);
  const nicheBusy = isNiche && !!(nicheLoading || {})[`${panel.oppIndex}.${panel.nicheIndex}`];
  // Filho de um lançador: rótulo reflete o pai (Mar -> Subnicho; Dobra -> Dobra).
  const typeLabel = isNiche
    ? (op.tipo === 'mar_oportunidades' ? t('dobraNicho.subtipo_subnicho') : t('dobraNicho.type_dobra'))
    : (op.tipo === 'mar_de_ideias'
        ? `${t('dobraNicho.type_mar_de_ideias')} · ${t(`dobraNicho.subtipo_${op.subtipo || 'nova_visao'}`)}`
        : t(`dobraNicho.type_${op.tipo}`, op.tipo));
  const heading = isNiche ? niche?.nome : op.label;
  // Para um filho de lançador: "Ideia de tema alvo" (cenário concreto) antes dos
  // títulos. Para oportunidade individual: o conceito.
  const ideiaTema = isNiche ? (niche?.ideia_tema || niche?.por_que_encaixa) : null;
  const lead = isNiche ? null : report.conceito;
  // Contexto-âncora do CANAL: toda projeção (nova visão, subnicho, dobra...) é
  // derivada do DNA do canal — mostra o nicho/público de origem pra ancorar.
  const anc = analysis?.result?.ancoragem || {};
  const channelName = analysis?.result?.dossier?.nome || analysis?.channel_title || '';
  // Identidade do ALVO (protagonista) + dica de aplicabilidade. O nicho ORIGINAL
  // vira só procedência discreta no rodapé.
  const recorteAlvo = isNiche ? niche?.recorte_alvo : report?.recorte_alvo;
  const comoAplicar = isNiche ? niche?.como_aplicar : report?.como_aplicar;
  const origem = anc.nicho_principal;   // de onde a fórmula veio (procedência)
  // Confiança DESTA oportunidade + porquê (sinal que a IA já escreveu). Dobra/
  // Adaptação são projeções cross-niche -> sempre hipótese a testar.
  const opConf = op?.confianca;
  const opConfLines = [
    op?.sinal_de_oportunidade || op?.rationale,
    (op?.tipo === 'dobra' || op?.tipo === 'adaptacao_mercado') ? t('dobraNicho.confidence_hypothesis') : '',
  ];

  // Drawer NÃO-bloqueante: sem scrim e sem aria-modal — o grafo continua
  // clicável ao lado (o <main> recebe paddingRight pra "abrir espaço"), então dá
  // pra selecionar outro círculo com o painel aberto. Fecha por Esc / botão X.
  return (
    <div
      role="dialog"
      aria-label={heading || typeLabel}
      style={{
        position: 'fixed', top: 60, right: 0, bottom: 0, width: 'min(560px, 96vw)', zIndex: 51,
        background: 'var(--dp-bg-card)', borderLeft: '1px solid var(--dp-border-subtle)',
        boxShadow: '-16px 0 48px rgba(0,0,0,0.45)', overflowY: 'auto', padding: '28px 30px',
        fontFamily: 'var(--dp-font)', animation: 'dp-slideInRight 0.25s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('dobraNicho.idea_prefix')} · {typeLabel}</div>
            <ConfBadge level={opConf} lines={opConfLines} t={t} />
          </div>
          <h2 style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 700, color: 'var(--dp-text-primary)', lineHeight: 1.25 }}>{heading}</h2>
          {recorteAlvo && <div style={{ marginTop: 6, fontSize: 13, color: 'var(--dp-text-secondary)', lineHeight: 1.4 }}>{recorteAlvo}</div>}
        </div>
        <button
          ref={closeRef}
          onClick={onClose}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dp-bg-card-hover)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dp-bg-primary)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
          style={{ background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--dp-text-secondary)', flexShrink: 0, transition: 'background 0.15s, color 0.15s' }}
        ><X size={18} /></button>
      </div>

      {/* Identidade explícita do nicho-alvo (dobra): família + recorte + público. */}
      {isNiche && (niche?.nicho || niche?.subnicho || niche?.publico_alvo) && (
        <div style={{ background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', borderRadius: 10, padding: '13px 15px', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            [t('dobraNicho.target_niche'), niche?.nicho],
            [t('dobraNicho.target_subniche'), niche?.subnicho],
            [t('dobraNicho.target_audience'), niche?.publico_alvo],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--dp-text-primary)' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}: </span>
              {value}
            </div>
          ))}
        </div>
      )}

      {op.tipo === 'adaptacao_mercado' && !isNiche && (
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 10, padding: '11px 13px', marginBottom: 20, fontSize: 12.5, color: 'var(--dp-text-primary)', lineHeight: 1.45 }}>
          <AlertTriangle size={16} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 1 }} />
          {t('dobraNicho.adaptacao_hypothesis')}
        </div>
      )}

      {lead && <p style={{ color: 'var(--dp-text-secondary)', fontSize: 14, lineHeight: 1.55, marginBottom: 22 }}>{lead}</p>}

      {/* Ideia de tema alvo: o cenário concreto do nicho-alvo, antes dos títulos. */}
      {isNiche && ideiaTema && (
        <div style={{ background: 'linear-gradient(135deg, rgba(13,174,255,0.06), var(--dp-bg-primary))', border: '1px solid var(--dp-border-subtle)', borderRadius: 10, padding: '13px 15px', marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{t('dobraNicho.target_idea')}</div>
          <div style={{ fontSize: 14, color: 'var(--dp-text-primary)', lineHeight: 1.55 }}>{ideiaTema}</div>
        </div>
      )}

      {/* Como aplicar na prática: a dica de aplicabilidade (novo canal, série, idioma...) */}
      {comoAplicar && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'rgba(13,174,255,0.07)', border: '1px solid rgba(13,174,255,0.22)', borderRadius: 10, padding: '13px 15px', marginBottom: 22 }}>
          <Rocket size={17} style={{ color: 'var(--dp-blue-500)', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{t('dobraNicho.how_to_apply')}</div>
            <div style={{ fontSize: 14, color: 'var(--dp-text-primary)', lineHeight: 1.5 }}>{comoAplicar}</div>
          </div>
        </div>
      )}

      {/* Ordem consumível: o ENTREGÁVEL (títulos prontos) primeiro; depois o
          porquê (preserva/muda), a equivalência e por fim os alertas. */}
      {(nicheBusy || (analyzing && isNiche && titulos.length === 0)) && titulos.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--dp-text-secondary)', fontSize: 14, marginBottom: 22 }}>
          <Loader2 size={16} className="animate-spin" style={{ color: 'var(--dp-blue-500)' }} />
          {t('dobraNicho.generating_titles')}
        </div>
      ) : titulos.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{t('dobraNicho.ready_titles')}</div>
          {titulos.map((tt, i) => <TitleCard key={i} text={tt} />)}
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}><PillList title={t('dobraNicho.preserves')} items={report.o_que_preserva_do_original} color="#34D399" /></div>
        <div style={{ flex: '1 1 200px' }}><PillList title={t('dobraNicho.changes')} items={report.o_que_muda} color="#F59E0B" /></div>
      </div>

      {mapa.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>{t('dobraNicho.equivalence')}</div>
          {mapa.slice(0, 6).map((m, i) => (
            <div key={i} style={{ background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', borderRadius: 10, padding: '11px 13px', marginBottom: 8 }}>
              <div style={{ fontSize: 12.5, color: 'var(--dp-text-secondary)', lineHeight: 1.4 }}>{m.titulo_original}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '5px 0' }}>
                <ArrowDown size={13} style={{ color: 'var(--dp-blue-500)' }} />
                {m.elemento_preservado && <span style={{ fontSize: 11, color: 'var(--dp-blue-500)' }}>{m.elemento_preservado}</span>}
              </div>
              <div style={{ fontSize: 14, color: 'var(--dp-text-primary)', fontWeight: 600, lineHeight: 1.4 }}>{m.titulo_novo}</div>
            </div>
          ))}
        </div>
      )}

      <PillList title={t('dobraNicho.break_alerts')} items={report.alertas_de_quebra} color="#F87171" />

      {/* Procedência discreta (rodapé): de onde a fórmula veio, aplicada no alvo. */}
      {origem && (
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid var(--dp-border-subtle)', fontSize: 11.5, color: 'var(--dp-text-tertiary)', lineHeight: 1.45 }}>
          {t('dobraNicho.procedence_line', { origem: channelName ? `${origem} (${channelName})` : origem, alvo: heading })}
        </div>
      )}
    </div>
  );
}
